import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { FormControlLabel, Switch, Typography } from '@mui/material';
import { DeleteIcon, PlusIcon } from '@sharedCR/coreDesignSystem/icons';
import AddButton from 'app/shared/components/common/AddButton';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FieldTypesEnum, LoanEditorActionsEnum } from 'constants/loanActionEditor';
import { deleteByIndex, setIn } from 'utils/data';

import { selectTimedSequenceSchema } from 'store/system/selectors';

import ExpressionField from '../../form/ExpressionField';
import { loanManagementTestIds } from '../loanManagement.testIds';

import useFormContext from './hooks/useFormContext';
import {
  CHAR_CODE_LIMIT_2,
  DIGIT_CHAR_CODE_START,
  wrapperClassname,
} from './LoanActionItem.constants';
import { DynamicValueInput } from './LoanActionItems';
import {
  getLoanEditorActionInitialItem,
  LoanEditorItemInitialValueByType,
  LoanEditorItemsKeyByType,
} from './LoanActionsGroup.utils';

const AddButtonLabelByType = {
  [LoanEditorActionsEnum.SET_FIELD_STATIC]: 'Add Application Value',
  [LoanEditorActionsEnum.ADD_TAG]: 'Add Tag Entity',
  [LoanEditorActionsEnum.REMOVE_TAG]: 'Add Tag Entity',
  [LoanEditorActionsEnum.ORDER_UPDATE_REPORT_MANUAL]: 'Add Report Entity',
  [LoanEditorActionsEnum.ORDER_UPDATE_REPORT_AUTO]: 'Add Report Entity',
  [LoanEditorActionsEnum.SET_FIELD_DYNAMIC]: 'Add Loan Editor Field',
  [LoanEditorActionsEnum.SAVE_PENDING_CHANGES]: 'Add Permission',
};

const initialTimedValues = {
  executionDelay: {
    hours: 0,
    minutes: 0,
  },
  isCustomStopExpression: false,
  userFriendlyStopExpression: '',
};

const LoanActionItem = ({
  actionIndex,
  isDisabled = false,
  isEditAllowed,
  isMultipleEntityAllowed = true,
  label = 'Action',
  children,
  options,
  getOptionDisabled,
  rootPath,
}) => {
  const timedSequenceSchemaParams = useSelector(selectTimedSequenceSchema);

  const actionKey = `${rootPath}[${actionIndex}]`;
  const itemTypePath = `${actionKey}.itemType`;
  const timedSequencePath = `${actionKey}.timedSequenceOptions`;
  const delayIntervalPath = `${timedSequencePath}.executionDelay`;
  const isCustomStopExpressionPath = `${timedSequencePath}.isCustomStopExpression`;
  const customStopExpressionPath = `${timedSequencePath}.userFriendlyStopExpression`;
  const delayIntervalHoursPath = `${delayIntervalPath}.hours`;
  const delayIntervaMinutesPath = `${delayIntervalPath}.minutes`;

  const {
    form,
    setInForm,
    setValidationErrors,
    validationErrors,
    handleChange,
    validateField,
    staticValidateField,
  } = useFormContext();
  const actionType = _.get(form, itemTypePath)?.value;
  const addLabel = AddButtonLabelByType[actionType];
  const isAddButtonDisplayed = isMultipleEntityAllowed && isEditAllowed;
  const { isTimedSequence } = form;
  const isFirstAction = actionIndex === 0;
  const isDelayInterval = !!_.get(form, delayIntervalPath);
  const isCustomStopExpression = _.get(form, isCustomStopExpressionPath);
  const customStopExpression = _.get(form, customStopExpressionPath);

  useEffect(() => {
    if (!isTimedSequence && isDelayInterval) {
      return;
    }

    setInForm(timedSequencePath, initialTimedValues);
  }, [setInForm, isTimedSequence, isDelayInterval, timedSequencePath]);

  const deleteActionItem = useCallback(() => {
    setInForm(rootPath, deleteByIndex(form[rootPath], actionIndex));
    setValidationErrors(errors =>
      setIn(errors, rootPath, deleteByIndex(_.get(errors, rootPath, []), actionIndex)),
    );
  }, [actionIndex, form, rootPath, setInForm, setValidationErrors]);

  const onAddNewActionEntity = useCallback(() => {
    const initialValue = LoanEditorItemInitialValueByType[actionType];
    const path = `${actionKey}.${LoanEditorItemsKeyByType[actionType]}`;
    const items = _.get(form, path);

    setInForm(`${path}[${items.length}]`, initialValue);
  }, [actionKey, form, actionType, setInForm]);

  const onItemTypeChange = useCallback(
    (__, { value }) =>
      Object.entries(getLoanEditorActionInitialItem(value)).forEach(([key, initialItem]) => {
        setInForm(`${actionKey}.${key}`, initialItem);
      }),
    [actionKey, setInForm],
  );

  const handleKeyPress = useCallback(event => {
    const { charCode } = event;

    if (charCode < DIGIT_CHAR_CODE_START || charCode > CHAR_CODE_LIMIT_2) {
      event.preventDefault();
    }
  }, []);

  const onTimeChange = useCallback(
    ({ target: { name, value } }) => {
      if (value < 0) {
        return;
      }

      setInForm(name, value);
      validateField(name, value);
    },
    [setInForm, validateField],
  );

  const onCustomStopConditionToggleChange = useCallback(
    e => {
      const { checked } = e.target;

      if (!checked) {
        setInForm(customStopExpressionPath, '');
      }

      setInForm(isCustomStopExpressionPath, checked);
    },
    [customStopExpressionPath, isCustomStopExpressionPath, setInForm],
  );

  return (
    <div className={wrapperClassname} key={actionKey}>
      {isTimedSequence && (
        <>
          {!isFirstAction && (
            <>
              <Typography variant="regular12">Delay Interval</Typography>
              <div className="flex gap-8">
                <TextFieldCDS
                  required
                  data-testid={loanManagementTestIds.LOAN_MANAGEMENT_FORM_HOURS_INPUT}
                  error={
                    !!validationErrors.actions?.[actionIndex]?.timedSequenceOptions?.executionDelay
                      ?.hours
                  }
                  helperText={
                    validationErrors.actions?.[actionIndex]?.timedSequenceOptions?.executionDelay
                      ?.hours
                  }
                  id="hours"
                  label="Hours"
                  name={delayIntervalHoursPath}
                  type="number"
                  value={form.actions?.[actionIndex]?.timedSequenceOptions?.executionDelay.hours}
                  variant="outlined"
                  onChange={onTimeChange}
                  onKeyPress={handleKeyPress}
                />
                <TextFieldCDS
                  required
                  data-testid={loanManagementTestIds.LOAN_MANAGEMENT_FORM_MINUTES_INPUT}
                  error={
                    !!validationErrors.actions?.[actionIndex]?.timedSequenceOptions?.executionDelay
                      ?.minutes
                  }
                  helperText={
                    validationErrors.actions?.[actionIndex]?.timedSequenceOptions?.executionDelay
                      ?.minutes
                  }
                  id="minutes"
                  label="Minutes"
                  name={delayIntervaMinutesPath}
                  type="number"
                  value={form.actions?.[actionIndex]?.timedSequenceOptions?.executionDelay.minutes}
                  variant="outlined"
                  onChange={onTimeChange}
                  onKeyPress={handleKeyPress}
                />
              </div>
            </>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={isCustomStopExpression}
                name={isCustomStopExpressionPath}
                onChange={onCustomStopConditionToggleChange}
              />
            }
            data-testid={loanManagementTestIds.getCustomStopConditionSwitchId(actionIndex)}
            label="Custom Stop Condition"
          />
          {isCustomStopExpression && (
            <ExpressionField
              isOnlyBooleanAllowed
              required
              error={
                !!validationErrors.actions?.[actionIndex]?.timedSequenceOptions
                  ?.userFriendlyStopExpression
              }
              handleChange={handleChange}
              helperText={
                validationErrors.actions?.[actionIndex]?.timedSequenceOptions
                  ?.userFriendlyStopExpression
              }
              label="Custom Stop Condition"
              name={customStopExpressionPath}
              schemaParams={timedSequenceSchemaParams}
              setInForm={setInForm}
              staticValidateField={staticValidateField}
              validateField={validateField}
              value={customStopExpression || ''}
            />
          )}
        </>
      )}
      <div className="flex items-center gap-8" key={`actionContainer-${actionIndex}`}>
        <DynamicValueInput
          required
          className="grow"
          disabled={isDisabled || !isEditAllowed}
          getOptionDisabled={getOptionDisabled}
          label={label}
          name={itemTypePath}
          options={options}
          type={FieldTypesEnum.SELECT}
          onChange={onItemTypeChange}
        />

        <div className="flex pt-20">
          {isEditAllowed && (
            <ButtonWithTooltip
              data-testid={loanManagementTestIds.getDeleteActionButtonId(actionIndex)}
              disabled={isDisabled}
              icon={DeleteIcon}
              size="medium"
              spanContainerClassName="flex items-center "
              title="Delete Action"
              onClick={deleteActionItem}
            />
          )}

          {isAddButtonDisplayed && (
            <AddButton
              ariaLabel={addLabel}
              dataTestId={loanManagementTestIds.CONTENT_VIEW_ADD_ACTION_ITEM}
              disabled={isDisabled}
              icon={PlusIcon}
              styleConfig="relative"
              onClick={onAddNewActionEntity}
            />
          )}
        </div>
      </div>

      <div className="ml-20 mb-10">{children}</div>
    </div>
  );
};

LoanActionItem.propTypes = {
  actionIndex: PropTypes.number.isRequired,
  children: PropTypes.node.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  options: PropTypes.array.isRequired,
  rootPath: PropTypes.string.isRequired,
  isMultipleEntityAllowed: PropTypes.bool,
  isDisabled: PropTypes.bool,
  getOptionDisabled: PropTypes.func,
  label: PropTypes.string,
};

export default LoanActionItem;
